import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Divider,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import EventForm from '../../components/EventForm';
import { Event, EVENT_ZONES } from '../../types/Event';
import { User, MEMBER_TYPES } from '../../types/User';
import { Participant } from '../../hooks/useParticipants';
import { useUserFinance } from '../../hooks/useUserFinance';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { SubscriptionDialog } from '../../components/SubscriptionDialog';

interface FamilyMember {
  id: string;
  firstName: string;
  lastName: string;
  nickname: string;
}

const ParticipantRow = ({
  userId,
  label,
  checked,
  onChange,
  event
}: {
  userId: string,
  label: string,
  checked: boolean,
  onChange: () => void,
  event: Event
}) => {
  const { totalPaid, isMember, loading } = useUserFinance(userId);

  if (loading) return <FormControlLabel control={<Checkbox checked={checked} disabled />} label={label} />;

  let statusText = "";
  let statusColor = "text.secondary";

  if (event.zone === EVENT_ZONES.WAN) {
    statusText = "🎮 Gratuit (Online)";
    statusColor = "info.main";
  } else if (isMember) {
    statusText = `✅ Accès Inclus (Plafond atteint : ${totalPaid}€)`;
    statusColor = "success.main";
  } else {
    statusText = `🪙 Participation : 5€ (Total versé : ${totalPaid}€)`;
    statusColor = "warning.main";
  }

  return (
    <Box sx={{ mb: 1 }}>
      <FormControlLabel
        control={<Checkbox checked={checked} onChange={onChange} />}
        label={
          <Box>
            <Typography variant="body1">{label}</Typography>
            <Typography variant="caption" sx={{ color: statusColor, fontWeight: 'bold' }}>
              {statusText}
            </Typography>
          </Box>
        }
      />
    </Box>
  );
};

// Composant pour afficher le statut de paiement des participants pour les admins
const AdminParticipantPaymentStatus = ({ participantId, eventId, isWanEvent, onAddSubscription }: { participantId: string, eventId?: string, isWanEvent: boolean, onAddSubscription: (id: string) => void }) => {
  const { totalPaid, isMember, loading, hasPaidForEvent } = useUserFinance(participantId, eventId);

  if (loading) {
    return <CircularProgress size={16} />;
  }

  if (isWanEvent) {
    return <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 'bold' }}>Gratuit (0€)</Typography>;
  }

  if (isMember || hasPaidForEvent) {
    return (
      <Tooltip title={`Total versé cette année: ${totalPaid}€`}>
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold' }}>
          ✅ Payé ({totalPaid}€)
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={`Total versé cette année: ${totalPaid}€ - Cliquer pour ajouter une cotisation`}>
      <Typography
        variant="caption"
        sx={{
          color: 'error.main',
          fontWeight: 'bold',
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline'
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          onAddSubscription(participantId);
        }}
      >
        ❌ Non payé ({totalPaid}€)
      </Typography>
    </Tooltip>
  );
};

interface EventDialogsProps {
  // Register Dialog
  openRegisterDialog: boolean;
  setOpenRegisterDialog: (open: boolean) => void;
  selectedEvent: Event | null;
  user: User | null;
  onRegisterEvent: (idsToAdd?: string[], idsToRemove?: string[]) => void; // Modifié pour accepter les deux listes

  // Participants Dialog
  openParticipantsList: boolean;
  setOpenParticipantsList: (open: boolean) => void;
  participants: Participant[];

  // Create/Edit Dialog
  openCreateDialog: boolean;
  setOpenCreateDialog: (open: boolean) => void;
  onEventSaved: () => void;

  // Non Member Dialog (Legacy?)
  openNonMemberDialog: boolean;
  setOpenNonMemberDialog: (open: boolean) => void;
}

const EventDialogs: React.FC<EventDialogsProps> = ({
  openRegisterDialog,
  setOpenRegisterDialog,
  selectedEvent,
  user,
  onRegisterEvent,
  openParticipantsList,
  setOpenParticipantsList,
  participants,
  openCreateDialog,
  setOpenCreateDialog,
  onEventSaved,
  openNonMemberDialog,
  setOpenNonMemberDialog
}) => {
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<string[]>([]);
  const { totalPaid, isMember, loading: loadingFinance } = useUserFinance(user?.uid);

  // Nouveaux états pour gérer la cotisation depuis la liste
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);
  const [subscriptionUserId, setSubscriptionUserId] = useState('');
  const [allUsers, setAllUsers] = useState<User[]>([]);

  // Charger la famille et tous les utilisateurs (pour les admins)
  useEffect(() => {
    if (user?.role === MEMBER_TYPES.ADMIN) {
      const fetchUsers = async () => {
        try {
          const querySnapshot = await getDocs(collection(db, 'users'));
          const usersData = querySnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() } as User))
            .filter(u => u.firstName && u.lastName && u.lastName.toUpperCase() !== 'PARTI')
            .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
          setAllUsers(usersData);
        } catch (error) {
          console.error("Erreur lors de la récupération des utilisateurs:", error);
        }
      };
      fetchUsers();
    }

    if (user) {
      const q = query(collection(db, 'users'), where('parentId', '==', user.uid));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const members = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as FamilyMember[];
        setFamilyMembers(members);
      });
      return () => unsubscribe();
    } else {
      setFamilyMembers([]);
    }
  }, [user]);

  // Initialiser la sélection à l'ouverture du dialog
  useEffect(() => {
    if (openRegisterDialog && selectedEvent && user) {
      const currentParticipants = selectedEvent.participants || [];
      const familyIds = familyMembers.map(m => m.id);

      // On pré-coche ceux qui sont déjà inscrits (Moi + Enfants)
      const initialSelection: string[] = [];

      // Moi
      if (currentParticipants.includes(user.uid)) {
        initialSelection.push(user.uid);
      }

      // Mes enfants
      familyMembers.forEach(member => {
        if (currentParticipants.includes(member.id)) {
          initialSelection.push(member.id);
        }
      });

      // Si personne de la famille n'est inscrit, on pré-coche au moins "Moi" par défaut pour faciliter
      if (initialSelection.length === 0 && !currentParticipants.includes(user.uid)) {
        initialSelection.push(user.uid);
      }

      setSelectedParticipantIds(initialSelection);
    }
  }, [openRegisterDialog, selectedEvent, user, familyMembers]);

  const handleCheckboxChange = (id: string) => {
    setSelectedParticipantIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(pid => pid !== id);
      } else {
        return prev.concat(id);
      }
    });
  };

  const handleConfirmRegister = () => {
    if (!selectedEvent || !user) return;

    const currentEventParticipants = selectedEvent.participants || [];

    // Qui on veut avoir au final
    const desiredIds = selectedParticipantIds;

    // Calcul des ajouts : Ceux qui sont dans desired mais pas dans current
    const idsToAdd = desiredIds.filter(id => !currentEventParticipants.includes(id));

    // Calcul des suppressions : 
    // Ceux qui sont dans current MAIS pas dans desired
    // ET qui font partie de "ma tribu" (Moi + Famille)
    // (On ne veut pas supprimer des inconnus par erreur)
    const myTribeIds = [user.uid, ...familyMembers.map(m => m.id)];

    const idsToRemove = currentEventParticipants.filter(id =>
      myTribeIds.includes(id) && !desiredIds.includes(id)
    );

    onRegisterEvent(idsToAdd, idsToRemove);
  };

  const hasFamily = familyMembers.length > 0;

  return (
    <>
      <Dialog
        open={openRegisterDialog}
        onClose={() => setOpenRegisterDialog(false)}
        maxWidth={hasFamily ? 'xs' : 'sm'} // Plus petit si c'est juste une liste
      >
        <DialogTitle>
          {selectedEvent?.title}
        </DialogTitle>
        <DialogContent>
          {/* Information Financière - Mode Solo uniquement */}
          {selectedEvent && user && !selectedEvent.participants?.includes(user.uid) && !loadingFinance && !hasFamily && (
            <Box sx={{ mb: 2 }}>
              {selectedEvent.zone === EVENT_ZONES.WAN ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>🌐 Mode Online (Gratuit)</strong><br />
                  C'est une session à distance, aucune participation aux frais n'est requise.
                </Alert>
              ) : isMember ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <strong>✅ Accès Inclus</strong><br />
                  Vous avez atteint le plafond de soutien annuel ({totalPaid}€).<br />
                  Aucune participation n'est demandée pour cet événement.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>🪙 Participation requise : 5€</strong><br />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Vos participations se cumulent : dès que vous atteignez 10€ (soit 2 événements), l'accès devient gratuit pour le reste de l'année.
                  </Typography>
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                    (Total actuel versé cette année : {totalPaid}€)
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {!hasFamily ? (
            <DialogContentText>
              {selectedEvent && selectedEvent.participants?.includes(user?.uid || '')
                ? 'Voulez-vous vous désinscrire de cet événement ?'
                : 'Voulez-vous vous inscrire à cet événement ?'
              }
            </DialogContentText>
          ) : (
            <Box>
              <DialogContentText sx={{ mb: 2 }}>
                Qui participe à l'événement ?
              </DialogContentText>

              {/* Info générale sur les tarifs si pas WAN */}
              {selectedEvent && selectedEvent.zone !== EVENT_ZONES.WAN && (
                <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                  <Typography variant="caption" sx={{ lineHeight: 1.2, display: 'block' }}>
                    ℹ️ Participation de 5€ par personne. Gratuit au-delà de 10€ cumulés sur l'année.
                  </Typography>
                </Alert>
              )}

              <FormGroup>
                {/* Moi (Chef de tribu) */}
                {user && selectedEvent && (
                  <ParticipantRow
                    userId={user.uid}
                    label={`Moi (${user.nickname || user.firstName})`}
                    checked={selectedParticipantIds.includes(user.uid)}
                    onChange={() => user && handleCheckboxChange(user.uid)}
                    event={selectedEvent}
                  />
                )}

                {familyMembers.length > 0 && <Divider sx={{ my: 1 }} />}

                {/* Enfants */}
                {familyMembers.map(member => (
                  selectedEvent && (
                    <ParticipantRow
                      key={member.id}
                      userId={member.id}
                      label={`${member.firstName} (${member.nickname})`}
                      checked={selectedParticipantIds.includes(member.id)}
                      onChange={() => handleCheckboxChange(member.id)}
                      event={selectedEvent}
                    />
                  )
                ))}
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRegisterDialog(false)}>Annuler</Button>
          {hasFamily ? (
            <Button onClick={handleConfirmRegister} color="primary" variant="contained">
              Valider les inscriptions
            </Button>
          ) : (
            <Button
              onClick={() => {
                // Logique simplifiée pour le cas sans famille : Toggle l'utilisateur courant
                if (selectedEvent && user) {
                  if (selectedEvent.participants?.includes(user.uid)) {
                    onRegisterEvent([], [user.uid]); // Désinscription
                  } else {
                    onRegisterEvent([user.uid], []); // Inscription
                  }
                }
              }}
              color="primary"
              autoFocus
            >
              {selectedEvent && selectedEvent.participants?.includes(user?.uid || '')
                ? 'Se désinscrire'
                : 'S\'inscrire'
              }
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={openParticipantsList}
        onClose={() => setOpenParticipantsList(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: { xs: 'auto', sm: '500px' },
            m: { xs: 2, sm: 'auto' },
            bgcolor: '#333',
            color: '#fff'
          }
        }}
      >
        <DialogTitle>
          Participants à {selectedEvent?.title}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 1, sm: 3 }, py: 2 }}>
          {participants.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {participants.map((participant, index) => (
                <React.Fragment key={participant.id}>
                  <Box
                    sx={{
                      // Mobile : colonne / PC : grille alignée
                      display: { xs: 'flex', sm: 'grid' },
                      flexDirection: 'column',
                      gridTemplateColumns: user?.role === MEMBER_TYPES.ADMIN
                        ? '1fr 1fr 1fr auto'
                        : '1fr 1fr 1fr',
                      alignItems: 'center',
                      py: 1.5,
                      px: { xs: 1, sm: 2 },
                      gap: { xs: 0.5, sm: 0 },
                      backgroundColor: participant.id === user?.uid ? 'rgba(25, 118, 210, 0.2)' : 'transparent',
                      borderRadius: 1
                    }}
                  >
                    {/* Colonne 1 : Pseudo / Identité */}
                    <Box sx={{ minWidth: 0, pr: { sm: 2 } }}>
                      <Typography sx={{ color: '#fff', fontWeight: 'bold', wordBreak: 'break-word' }}>
                        {user?.role === MEMBER_TYPES.ADMIN
                          ? (participant.firstName ? `${participant.firstName} ${participant.lastName || ''} (${participant.nickname || 'Sans pseudo'})` : participant.name)
                          : (participant.nickname || participant.name.split(' ').pop() || 'Membre')
                        }
                      </Typography>
                    </Box>

                    {/* Colonne 2 : Équipe */}
                    <Box sx={{ pr: { sm: 2 } }}>
                      <Typography variant="body2" sx={{ color: '#aaa' }}>
                        {participant.team || 'Sans équipe'}
                      </Typography>
                    </Box>

                    {/* Colonne 3 : Ville */}
                    <Box>
                      <Typography variant="body2" sx={{ color: '#aaa' }}>
                        {participant.city || 'Ville inconnue'}
                      </Typography>
                    </Box>

                    {/* Colonne 4 : Statut de paiement (Admin uniquement) */}
                    {user?.role === MEMBER_TYPES.ADMIN && (
                      <Box sx={{
                        mt: { xs: 1, sm: 0 },
                        p: { xs: 1, sm: 0 },
                        bgcolor: { xs: 'rgba(255,255,255,0.05)', sm: 'transparent' },
                        borderRadius: 1,
                        width: { xs: '100%', sm: 'auto' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: { xs: 'space-between', sm: 'flex-end' }
                      }}>
                        <Typography variant="body2" sx={{ color: '#aaa', display: { xs: 'inline', sm: 'none' } }}>
                          Statut :
                        </Typography>
                        <AdminParticipantPaymentStatus
                          participantId={participant.id}
                          eventId={selectedEvent?.id}
                          isWanEvent={selectedEvent?.zone === EVENT_ZONES.WAN}
                          onAddSubscription={(id: string) => {
                            setSubscriptionUserId(id);
                            setOpenSubscriptionDialog(true);
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                  {index < participants.length - 1 && (
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 0.5 }} />
                  )}
                </React.Fragment>
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: '#aaa', textAlign: 'center', py: 2 }}>
              Aucun participant inscrit
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenParticipantsList(false)}
            sx={{ color: '#ff0000' }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openNonMemberDialog}
        onClose={() => setOpenNonMemberDialog(false)}
        aria-labelledby="non-member-dialog-title"
        aria-describedby="non-member-dialog-description"
      >
        <DialogTitle id="non-member-dialog-title" sx={{ color: '#1976d2' }}>
          Bienvenue sur Rezone Gaming !
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="non-member-dialog-description">
            Pour participer à nos événements, nous vous invitons à devenir adhérent de l'association.
            <br /><br />
            Cependant, vous êtes les bienvenus pour venir découvrir l'association lors de cet événement !
            {!user && (
              <><br /><br />
                <strong>Connectez-vous pour pouvoir interagir avec les événements.</strong></>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNonMemberDialog(false)} color="primary">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedEvent?.id ? 'Modifier l\'événement' : 'Créer un événement'}
        </DialogTitle>
        <DialogContent>
          <EventForm
            event={selectedEvent}
            onClose={() => setOpenCreateDialog(false)}
            onSave={() => {
              setOpenCreateDialog(false);
              onEventSaved();
            }}
          />
        </DialogContent>
      </Dialog>

      <SubscriptionDialog
        open={openSubscriptionDialog}
        onClose={() => {
          setOpenSubscriptionDialog(false);
          setSubscriptionUserId('');
        }}
        users={allUsers}
        selectedUserId={subscriptionUserId}
        selectedEventId={selectedEvent?.id}
      />
    </>
  );
};

export default EventDialogs;
